import HttpFactory from "~/repository/factory";
import { ENDPOINTS } from "~/repository/endpoints";
import type {
  GoogleCalendarImportedEvent,
  GoogleCalendarListItem,
  GoogleCalendarClassSeries,
  GoogleCalendarOAuthStartResponse,
  GoogleCalendarSelectionResponse,
  GoogleCalendarStatusResponse,
  GoogleCalendarSeriesLinkInput,
  GoogleCalendarSeriesScheduleInput,
} from "~/types/calendar";

class CalendarModule extends HttpFactory {
  private googleSeriesActionUrl(seriesId: number, action: string): string {
    return `${ENDPOINTS.CALENDAR.GOOGLE_SERIES_DETAIL.replace(
      "{seriesId}",
      String(seriesId),
    )}${action}/`;
  }

  async getGoogleStatus(): Promise<GoogleCalendarStatusResponse> {
    return await this.get<GoogleCalendarStatusResponse>(
      ENDPOINTS.CALENDAR.GOOGLE_STATUS,
    );
  }

  async startGoogleOAuth(
    returnPath?: string,
  ): Promise<GoogleCalendarOAuthStartResponse> {
    return await this.post<GoogleCalendarOAuthStartResponse>(
      ENDPOINTS.CALENDAR.GOOGLE_OAUTH_START,
      returnPath ? { return_path: returnPath } : {},
    );
  }

  async listGoogleCalendars(): Promise<GoogleCalendarListItem[]> {
    return await this.get<GoogleCalendarListItem[]>(
      ENDPOINTS.CALENDAR.GOOGLE_CALENDARS,
    );
  }

  async selectGoogleCalendar(
    calendarId: string,
    confirmLinkedSeriesLoss = false,
  ): Promise<GoogleCalendarSelectionResponse> {
    return await this.patch<GoogleCalendarSelectionResponse>(
      ENDPOINTS.CALENDAR.GOOGLE_SELECTION,
      {
        calendar_id: calendarId,
        confirm_linked_series_loss: confirmLinkedSeriesLoss,
      },
    );
  }

  async syncGoogleCalendar(): Promise<{ queued: boolean }> {
    return await this.post<{ queued: boolean }>(
      ENDPOINTS.CALENDAR.GOOGLE_SYNC,
      {},
    );
  }

  async disconnectGoogleCalendar(
    confirmLinkedSeriesLoss = false,
  ): Promise<void> {
    return await this.post<void>(ENDPOINTS.CALENDAR.GOOGLE_DISCONNECT, {
      confirm_linked_series_loss: confirmLinkedSeriesLoss,
    });
  }

  async listGoogleEvents(params: {
    time_min: string;
    time_max: string;
  }): Promise<GoogleCalendarImportedEvent[]> {
    return await this.get<GoogleCalendarImportedEvent[]>(
      ENDPOINTS.CALENDAR.GOOGLE_EVENTS,
      params,
    );
  }

  async listGoogleSeries(
    params: {
      include_dismissed?: boolean;
    } = {},
  ): Promise<GoogleCalendarClassSeries[]> {
    return await this.get<GoogleCalendarClassSeries[]>(
      ENDPOINTS.CALENDAR.GOOGLE_SERIES,
      params,
    );
  }

  async linkGoogleSeries(
    seriesId: number,
    payload: GoogleCalendarSeriesLinkInput,
  ): Promise<GoogleCalendarClassSeries> {
    return await this.post<GoogleCalendarClassSeries>(
      this.googleSeriesActionUrl(seriesId, "link"),
      payload,
    );
  }

  async dismissGoogleSeries(
    seriesId: number,
  ): Promise<GoogleCalendarClassSeries> {
    return await this.post<GoogleCalendarClassSeries>(
      this.googleSeriesActionUrl(seriesId, "dismiss"),
      {},
    );
  }

  async reopenGoogleSeries(
    seriesId: number,
  ): Promise<GoogleCalendarClassSeries> {
    return await this.post<GoogleCalendarClassSeries>(
      this.googleSeriesActionUrl(seriesId, "reopen"),
      {},
    );
  }

  async updateGoogleSeriesSchedule(
    seriesId: number,
    payload: GoogleCalendarSeriesScheduleInput,
  ): Promise<GoogleCalendarClassSeries> {
    return await this.patch<GoogleCalendarClassSeries>(
      this.googleSeriesActionUrl(seriesId, "schedule"),
      payload,
    );
  }

  async unlinkGoogleSeries(
    seriesId: number,
  ): Promise<GoogleCalendarClassSeries> {
    return await this.delete<GoogleCalendarClassSeries>(
      this.googleSeriesActionUrl(seriesId, "link"),
    );
  }
}

export default CalendarModule;
